import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Sparkles, ShieldCheck, Clock3 } from "lucide-react";
import { BIZ, IMAGES } from "@/constants/content";
import { MaskLine, EASE } from "@/components/Reveal";

const STATS = [
  { k: "~1 hr", v: "typical on-site repair" },
  { k: "24–72 hr", v: "ship-in turnaround, after we receive them" },
  { k: "10+ yrs", v: "hands-on experience" },
  { k: "200+ hrs", v: "factory training" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section ref={ref} id="top" className="blueprint relative overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-24 h-[480px] w-[480px] rounded-full bg-brandsky/20 blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 top-64 h-[420px] w-[420px] rounded-full bg-brandpink/15 blur-[120px]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-paper" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-36 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28 lg:pt-44">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-ink/10 bg-white/80 py-1.5 pl-2 pr-4 shadow-sm backdrop-blur"
            data-testid="hero-eyebrow"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping-dot rounded-full bg-brandleaf opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brandleaf" />
            </span>
            <span className="eyebrow text-ink/70">Mobile workshop van · Buffalo · Erie · Rochester</span>
          </motion.div>

          <h1 className="font-display mt-6 text-[clamp(2.5rem,4.4vw,4.2rem)] font-extrabold leading-[0.98] tracking-[-0.02em] text-ink">
            <MaskLine delay={0.15}>Broken handpieces,</MaskLine>
            <MaskLine delay={0.27}>back in your hands</MaskLine>
            <MaskLine delay={0.39}>
              in <span className="text-brandpink">about an hour.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
            data-testid="hero-subline"
          >
            JSL is the family-run handpiece shop that comes to you. Our mobile workshop van repairs pneumatics,
            electrics, latch heads and nosecones <strong className="font-semibold text-ink">right in your parking lot</strong> —
            all makes, all models, OEM-spec parts.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a
              href="#intake"
              data-testid="hero-intake-button"
              className="btn-lift group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-4 text-[15px] font-bold text-white shadow-[0_16px_36px_rgba(22,50,79,0.3)] hover:bg-abyss"
            >
              Get my handpieces fixed
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={BIZ.jacob.tel}
              data-testid="hero-call-button"
              className="btn-lift inline-flex items-center gap-2.5 rounded-full border border-ink/20 bg-white/80 px-7 py-4 text-[15px] font-bold text-ink backdrop-blur hover:border-ink/50"
            >
              Call or text {BIZ.jacob.phone}
            </a>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
            className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 sm:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.k} className="bg-white/85 px-5 py-4 backdrop-blur">
                <div className="font-mono text-lg font-bold text-ink">{s.k}</div>
                <div className="mt-0.5 text-[11.5px] font-medium leading-snug text-slate-500">{s.v}</div>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45, ease: EASE }}
            className="relative mx-auto max-w-[460px]"
          >
            <motion.div style={{ y: yMain }} className="relative">
              <div className="overflow-hidden rounded-[26px] border border-ink/10 bg-white shadow-[0_30px_80px_rgba(22,50,79,0.22)]">
                <img
                  src={IMAGES.hero}
                  alt="Dental handpieces and equipment on a repair bench"
                  className="aspect-[4/3.4] w-full object-cover"
                  data-testid="hero-image"
                />
                <div className="pointer-events-none absolute inset-0 rounded-[26px] bg-[radial-gradient(120%_90%_at_20%_0%,rgba(59,191,230,0.28),transparent_55%)] mix-blend-screen" />
              </div>

              <motion.div
                style={{ y: yCard }}
                className="absolute -bottom-10 -left-6 w-[46%] overflow-hidden rounded-2xl border-4 border-white bg-white shadow-[0_20px_50px_rgba(22,50,79,0.28)] sm:-left-12"
                data-testid="hero-overlay-card"
              >
                <img src={IMAGES.handpieces} alt="Handpiece turbines and repair parts" className="aspect-square w-full object-cover" />
              </motion.div>
            </motion.div>

            <motion.div
              style={{ y: yBadge }}
              className="absolute -right-3 top-8 rotate-3 rounded-2xl bg-brandpink px-5 py-3.5 text-white shadow-[0_18px_40px_rgba(234,76,137,0.4)] sm:-right-8"
              data-testid="hero-discount-badge"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                <span className="font-display text-xl font-extrabold leading-none">15% OFF</span>
              </div>
              <div className="mt-1 text-[11px] font-semibold tracking-wide opacity-90">your first repair order</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1, ease: EASE }}
              className="absolute -bottom-6 right-2 flex items-center gap-2.5 rounded-full border border-ink/10 bg-white/95 px-4 py-2.5 shadow-lg backdrop-blur"
              data-testid="hero-sameday-chip"
            >
              <Clock3 className="h-4 w-4 text-brandleaf-deep" />
              <span className="text-[12.5px] font-bold text-ink">Same-day, on-site repairs</span>
              <ShieldCheck className="h-4 w-4 text-brandsky-deep" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}